import React, { useEffect, useState } from 'react';
import { Mo, Dp } from './shared';

/**
 * LiquidFillGauge Component
 * A visually appealing liquid fill animation for displaying ratios/percentages
 * @param {number} percentage - The fill percentage (0-100)
 * @param {string} label - Label to display
 * @param {number} size - Container size in pixels
 * @param {string} liquidColor - Color of the liquid fill
 */
export function LiquidFillGauge({ percentage = 0, label = '', size = 200, liquidColor = '#00FFCC' }) {
  const [fillHeight, setFillHeight] = useState(0);
  
  useEffect(() => {
    // Trigger animation after mount
    const timer = setTimeout(() => {
      setFillHeight(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  // Determine status color
  const getStatusColor = () => {
    if (percentage <= 35) return '#22c55e'; // Good - green
    if (percentage <= 50) return '#eab308'; // Warning - yellow
    return '#ef4444'; // Danger - red
  };

  const statusColor = getStatusColor();
  const displayPercentage = Math.round(percentage);

  return (
    <div 
      style={{ 
        position: 'relative',
        width: size,
        height: size,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Glass Container */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '50%',
        boxShadow: 'inset 0 0 20px rgba(0, 0, 0, 0.5), 0 8px 32px rgba(0, 0, 0, 0.4)',
        overflow: 'hidden'
      }}>
        {/* Liquid Fill */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${fillHeight}%`,
          background: `linear-gradient(180deg, ${liquidColor} 0%, ${liquidColor}CC 100%)`,
          transition: 'height 2s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: `0 -4px 20px ${liquidColor}66`,
          '--fill-height': `${fillHeight}%`
        }} className="liquid-fill">
          {/* Wave Effect */}
          <svg 
            style={{
              position: 'absolute',
              top: -10,
              left: 0,
              width: '200%',
              height: 20,
            }}
            className="liquid-wave"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,10 Q250,0 500,10 T1000,10 V20 H0 Z"
              fill={liquidColor}
              opacity="0.7"
            />
          </svg>
        </div>

        {/* Shine Effect */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '30%',
          height: '30%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />
      </div>

      {/* Center Text */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)'
      }}>
        <Dp style={{ 
          fontSize: size * 0.25, 
          fontWeight: 900, 
          color: '#FFFFFF',
          lineHeight: 1,
          textShadow: `0 0 20px ${statusColor}88, 0 2px 8px rgba(0, 0, 0, 0.8)`
        }}>
          {displayPercentage}%
        </Dp>
        {label && (
          <Mo style={{ 
            fontSize: size * 0.06, 
            fontWeight: 700,
            color: 'rgba(255, 255, 255, 0.9)',
            letterSpacing: 1.5,
            marginTop: 4,
            textTransform: 'uppercase'
          }}>
            {label}
          </Mo>
        )}
      </div>
    </div>
  );
}
